import { useAppSelector } from '@redux/store';
import { ClientModel } from 'modules/map/types';
import type { ReplaceRoleAcl } from 'modules/account/types';
import { useMemo } from 'react';

// Cờ quyền cho các nút/màn trong Bản đồ thực đơn — mỗi cờ = 1 tài nguyên ACL cần CẢ read & list.
interface RoleAccessModel {
  customerProfile: boolean;
  abridgedMenu: boolean;
  nutriMenu: boolean;
  menuBalance: boolean;
  dailyInvoice: boolean;
  teacherMenu: boolean;
  foodTraceability: boolean;
}

// Registry data-driven: cờ -> object_key trong ACL content.v20241121.
// Thêm tài nguyên mới = thêm 1 dòng ở đây (+ 1 field ở RoleAccessModel), thay vì copy-paste 4 vòng × 7 if.
const RESOURCE_BY_FLAG: Record<keyof RoleAccessModel, string> = {
  customerProfile: 'customer-profile',
  abridgedMenu: 'abridged-menu',
  nutriMenu: 'nutrition-menu',
  menuBalance: 'detail-menu-balance',
  dailyInvoice: 'daily-consolidated-invoice',
  teacherMenu: 'teacher-menu',
  foodTraceability: 'food-traceability',
};
const FLAGS = Object.keys(RESOURCE_BY_FLAG) as (keyof RoleAccessModel)[];

const emptyAccess = (): RoleAccessModel => {
  const a = {} as RoleAccessModel;
  for (const f of FLAGS) a[f] = false;
  return a;
};

// OR hai tập quyền theo từng cờ.
const mergeAccess = (a: RoleAccessModel, b?: RoleAccessModel): RoleAccessModel => {
  if (!b) return a;
  const out = {} as RoleAccessModel;
  for (const f of FLAGS) out[f] = a[f] || b[f];
  return out;
};

// Gộp mọi acl của 1 vai trò: cờ = true nếu CÓ acl cấp cả read & list cho tài nguyên tương ứng.
const evalAcls = (acls?: ReplaceRoleAcl[] | null): RoleAccessModel => {
  const access = emptyAccess();
  for (const entry of acls ?? []) {
    const content = entry.acl?.content?.v20241121;
    if (!content) continue;
    for (const f of FLAGS) {
      const node = content[RESOURCE_BY_FLAG[f]] as { read?: boolean; list?: boolean } | undefined;
      if (node?.read && node?.list) access[f] = true;
    }
  }
  return access;
};

const useGetRole = (focusData?: ClientModel) => {
  const { userRoles: roleAssignments } = useAppSelector((state) => state.auth);

  const accessRights = useMemo(() => {
    if (!roleAssignments) return null;

    // Vai trò hệ thống: quyền toàn cục (không theo org).
    const all = emptyAccess();
    for (const a of roleAssignments.systemRoles ?? []) {
      const acc = evalAcls(a.systemRole.acls);
      for (const f of FLAGS) all[f] = all[f] || acc[f];
    }

    // Vai trò theo org (admin/client/gov): gộp quyền theo từng org đích.
    const byOpId = new Map<string, RoleAccessModel>();
    const applyPerOrg = (
      ids: (string | null | undefined)[] | null | undefined,
      acc: RoleAccessModel
    ) => {
      (ids ?? []).forEach((id) => {
        if (id == null) return;
        byOpId.set(id, mergeAccess(acc, byOpId.get(id)));
      });
    };

    for (const a of roleAssignments.adminRoles ?? []) {
      applyPerOrg([a.targetOrgId], evalAcls(a.adminRole.acls));
    }
    for (const a of roleAssignments.clientBusinessRoles ?? []) {
      applyPerOrg([a.targetOrgId], evalAcls(a.clientBusinessRole.acls));
    }
    for (const a of roleAssignments.govAgencyBusinessRoles ?? []) {
      applyPerOrg(a.includedClientIds, evalAcls(a.govAgencyBusinessRole.acls));
    }

    return { all, byOpId };
  }, [roleAssignments]);

  const roles = useMemo(() => {
    const retval = emptyAccess();
    if (focusData) {
      const perOrg = accessRights?.byOpId.get(focusData.operationCode);
      for (const f of FLAGS) {
        retval[f] = Boolean(accessRights?.all[f] || perOrg?.[f]);
      }
    }
    return retval;
  }, [focusData, accessRights]);

  return roles;
};
export default useGetRole;
